export const playerColorClass = (color, type) => {
    const prefix = {
        shadow: 'player_shadow_color_',
        bg_transparent: 'player_translucent_bg_color_',
        bg: 'player_bg_color_',
    }[type];
    return `${prefix}${color}`;
};
/**
 * Creates a range from 0 to n.
 *
 * @param {number} n maximum value in the range.
 * @return {Array<number>} an Array of numbers from 0 to n-1, inclusive.
 */
export const range = (n) => Array.from(Array(n).keys());
/**
 * Returns a new array consisting of elements only in both a and b.
 *
 * This preserves the order of the first array.
 *
 * @param {ReadonlyArray<T>} a: the first array
 * @param {ReadonlyArray<T>} b: the second array
 * @return {Array<T>} the intersection of both arrays
 */
export function intersection(a, b) {
    return a.filter((e) => b.includes(e));
}
/**
 * Returns true if a includes an element of b.
 *
 * @param {Array<T>} a: the first array
 * @param {Array<T>} b: the second array
 * @return {Boolean} true if a includes an element of b.
 */
export function hasIntersection(a, b) {
    return a.some((e) => b.includes(e));
}
/**
 * Returns elements in a that are not in b.
 *
 * Friendly to duplicates in an array.
 *
 * @param {Array} a the first array
 * @param {Array} b the second array
 */
export function oneWayDifference(a, b) {
    // Not optimized for large arrays.
    const remainder = [...a];
    for (const elem of b) {
        inplaceRemove(remainder, elem);
    }
    return remainder;
}
/**
 * Returns elements in neither A nor B.
 */
export function twoWayDifference(a, b) {
    return a
        .filter((x) => !b.includes(x))
        .concat(b.filter((x) => !a.includes(x)));
}
/**
 * Remove the `element` from `array`.
 *
 * Returns true if the element was removed from the array, false otherwise.
 */
export function inplaceRemove(array, element) {
    return inplaceRemoveIf(array, (e) => e === element) !== undefined;
}
/**
 * Remove the first element that satisfies the predicate from the array
 *.
 * Returns the removed element, or undefined if no element was removed.
 */
export function inplaceRemoveIf(array, predicate) {
    const idx = array.findIndex(predicate);
    if (idx === -1) {
        return undefined;
    }
    const element = array[idx];
    array.splice(idx, 1);
    return element;
}
export function sum(array) {
    return array.reduce((a, b) => a + b, 0);
}
/**
 * Creates an array of elements split into two groups,
 * the first of which contains elements predicate returns truthy for,
 * the second of which contains elements predicate returns falsey for.
 * The predicate is invoked with one argument: (value).
 *
 * @param {Array<T>} source The collection to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Array<Array<T>>} Returns the array of grouped elements. Passing group come first.
 */
export function partition(source, predicate) {
    return source.reduce((result, element) => {
        result[predicate(element) ? 0 : 1].push(element);
        return result;
    }, [[], []]);
}
/**
 * Zip two arrays of equal length.
 *
 * Unexpected behavior if the arrays have different length.
 */
export function zip(first, second) {
    return first.map((e, i) => [e, second[i]]);
}
/**
 * Returns `elem` if it is an array. If it is not an array, returns `[elem]`
 */
export function asArray(elem) {
    return Array.isArray(elem) ? elem : [elem];
}
export function definedOrThrow(obj) {
    if (obj === undefined) {
        throw new Error('object is undefined');
    }
    return obj;
}
export function deNull(array) {
    const output = [];
    for (const elem of array) {
        if (elem !== undefined) {
            output.push(elem);
        }
    }
    return output;
}
/**
 * Return a partial of |record| omitting entries whose value is 0.
 */
export function partialize(record) {
    const partial = {};
    for (const e in record) {
        if (record[e] !== 0) {
            partial[e] = record[e];
        }
    }
    return partial;
}
/**
 * Makes a copy of array, but then empties it.
 * Useful for moving contents.
 */
export function copyAndClear(array) {
    const copy = [...array];
    array.length = 0;
    return copy;
}
/**
 * Returns the name of any named item. Ideal for iterating with the Array.map and other iterative functions.
 */
export function toName(item) {
    return item.name;
}
/**
 * Returns the ID of any IDed item. Ideal for iterating with the Array.map and other iterative functions.
 */
export function toID(item) {
    return item.id;
}
/**
 * Return the names of an enum. For example, given
 *
 * enum Day {
 *   M = 'Monday',
 *   T = 'Tuesday',
 * }
 *
 * this returns ['M', 'T'].
 */
export function getEnumKeys(enumObject) {
    return Object.keys(enumObject);
}
/**
 * Return the values of an enum. For example, given
 *
 * enum Day {
 *   M = 'Monday',
 *   T = 'Tuesday',
 * }
 *
 * this returns ['Monday', 'Tuesday'], and is of type Array<Day>.
 */
export function getEnumStringValues(enumObject) {
    return Object.values(enumObject);
}
/**
 * Return the entries of an enum. For example, given
 *
 * enum Day {
 *   M = 'Monday',
 *   T = 'Tuesday',
 * }
 *
 * this returns [['M', 'Monday'], ['T', 'Tuesday']], and is of type Array<[string, Day]>.
 */
export function getEnumStringEntries(enumObject) {
    return Object.entries(enumObject);
}
export function cast(obj, klass) {
    if (klass === undefined) {
        if (obj !== undefined) {
            throw new Error(`Expected undefined, got type ${obj.constructor.name}`);
        }
        return undefined;
    }
    if (!(obj instanceof klass)) {
        throw new Error(`Not an instance of ${klass.name}: ${obj?.constructor?.name}`);
    }
    return obj;
}
/**
 * Fails to compile when `x` is reachable, which makes a switch over a union
 * exhaustive: adding a member to the union breaks the build at every switch
 * that doesn't handle it.
 */
export function assertNever(x) {
    throw new Error('unexpected value: ' + x);
}
